import { useId, type ReactNode } from 'react';
import { MagnifyingGlassIcon, MapPinIcon } from '@phosphor-icons/react';
import {
  PlaceField,
  type PlaceFieldCopy,
  type PlaceSuggestion,
} from '@voll-intelligence/intelligence-ui-kit';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import type {
  ChatUiMobilityPlannerField,
  ChatUiMobilityPlannerPlace,
  MobilityPlannerBlock,
  MobilityPlannerTripType,
} from '@voll-intelligence/genui-contract';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { LABEL, PlannerColumn, ROW, StaticPill } from './planner_parts.js';
import { PassengersStepper, TripTypeControl } from './planner_trip_controls.js';

type Place = ChatUiMobilityPlannerPlace;

/** O que todo campo de lugar do card recebe igual: a copy do painel, o grupo
 *  das sugestões, o texto vazio e a busca pelo port. */
export interface PlaceFieldShared {
  copy: PlaceFieldCopy;
  suggestionsLabel: string;
  emptyText: string;
  onSearch: (term: string) => Promise<readonly PlaceSuggestion[]>;
}

/** Lugar do wire → linha do painel compartilhado. */
function asSuggestion(place: Place, groupLabel?: string): PlaceSuggestion {
  return {
    id: place.ref,
    label: place.title,
    icon: 'place',
    ...(place.subtitle ? { description: place.subtitle } : {}),
    ...(groupLabel ? { groupLabel } : {}),
  };
}

/** Linha escolhida → lugar do wire (sugestão do agente ou resultado da busca). */
function asPlace(suggestion: PlaceSuggestion): Place {
  return {
    ref: suggestion.id,
    title: suggestion.label,
    ...(suggestion.description ? { subtitle: suggestion.description } : {}),
  };
}

/** Uma ponta do traslado: o `PlaceField` do DS com as sugestões do agente no
 *  zero-query. Com elas, o termo dito só nomeia o campo fechado: como rascunho,
 *  ele abriria já buscando no host e a busca crua trocaria as sugestões que o
 *  agente ranqueou. Sem sugestões (lugar não encontrado), o termo é o rascunho
 *  e abre buscando por ele. `columnLabel` é o rótulo visível da coluna; a linha
 *  da Volta não o repete (o campo segue nomeado por `label`). */
function PlannerPlaceField({
  label,
  columnLabel,
  className,
  field,
  value,
  copy,
  suggestionsLabel,
  emptyText,
  open,
  onSearch,
  onPick,
}: Readonly<{
  label: string;
  columnLabel?: string;
  className?: string;
  field: ChatUiMobilityPlannerField;
  value: Place | undefined;
  copy: PlaceFieldCopy;
  suggestionsLabel: string;
  emptyText: string;
  open: boolean;
  onSearch: (term: string) => Promise<readonly PlaceSuggestion[]>;
  onPick: (place: Place) => void;
}>): ReactNode {
  const zeroQuery = field.suggestions?.map((s) => asSuggestion(s, suggestionsLabel));
  const suggested = zeroQuery !== undefined && zeroQuery.length > 0;
  const term = value ? undefined : field.term;
  return (
    <PlannerColumn
      {...(columnLabel ? { label: columnLabel } : {})}
      {...(className ? { className } : {})}
      wide
    >
      <PlaceField
        label={label}
        {...(value ? { value: { title: value.title } } : {})}
        emptyText={suggested && term ? term : emptyText}
        {...(!suggested && term ? { draft: term } : {})}
        copy={copy}
        {...(suggested ? { zeroQuery } : {})}
        onSearch={onSearch}
        {...(value ? { selectedPlaceId: value.ref } : {})}
        onPick={(picked) => {
          onPick(asPlace(picked));
        }}
        icon={<MapPinIcon className="size-3.5" aria-hidden="true" />}
        variant="outline"
        defaultOpen={open}
      />
    </PlannerColumn>
  );
}

/** O Buscar: só o ícone na fileira larga, «Buscar carros» `xl` na largura toda
 *  no celular. Fica no Tab mesmo sem busca pronta (`aria-disabled`): diz o que
 *  falta (`missing`, a descrição) em vez de sumir do teclado. */
export function SearchButton({
  label,
  ready,
  missing,
  onSearch,
}: Readonly<{
  label: string;
  ready: boolean;
  missing: string | undefined;
  onSearch: () => void;
}>): ReactNode {
  const missingId = useId();
  return (
    <>
      <Button
        size="icon-lg"
        aria-label={label}
        aria-disabled={!ready || undefined}
        {...(missing ? { 'aria-describedby': missingId } : {})}
        className="shrink-0 rounded-full aria-disabled:cursor-default aria-disabled:opacity-50 @max-[theme(--breakpoint-sm)]:h-11 @max-[theme(--breakpoint-sm)]:w-full @max-[theme(--breakpoint-sm)]:text-[0.9375rem]"
        onClick={() => {
          if (ready) onSearch();
        }}
      >
        <MagnifyingGlassIcon
          className="size-4 @max-[theme(--breakpoint-sm)]:hidden"
          aria-hidden="true"
        />
        <span aria-hidden="true" className="hidden @max-[theme(--breakpoint-sm)]:inline">
          {label}
        </span>
      </Button>
      {missing && (
        <span id={missingId} hidden>
          {missing}
        </span>
      )}
    </>
  );
}

/** A pílula do Quando na fileira viva: o rótulo da coluna é `aria-hidden`, a
 *  pergunta vai junto da resposta. */
function WhenColumn({
  label,
  when,
  pending,
  showLabel = true,
}: Readonly<{ label: string; when?: string; pending: string; showLabel?: boolean }>): ReactNode {
  return (
    <PlannerColumn {...(showLabel ? { label } : {})} wide={false}>
      <StaticPill icon="when" text={when} emptyText={pending} srLabel={label} />
    </PlannerColumn>
  );
}

/** A linha de cima da corrida de cooperativa: o Quem (só leitura, no grid dos
 *  campos), o Sentido e as Pessoas. */
export function TripRow({
  who,
  tripType,
  passengers,
  onTripType,
  onPassengers,
}: Readonly<{
  who: string | undefined;
  tripType: MobilityPlannerTripType;
  passengers: number;
  onTripType: (value: MobilityPlannerTripType) => void;
  onPassengers: (value: number) => void;
}>): ReactNode {
  const { t } = useGenUiI18n();
  return (
    <div className="flex flex-wrap items-end gap-2">
      {who && (
        <PlannerColumn
          label={t('mobilityPlanner.who')}
          wide={false}
          className="w-auto max-w-60 @max-[theme(--breakpoint-sm)]:w-full"
        >
          <StaticPill icon="who" text={who} srLabel={t('mobilityPlanner.who')} />
        </PlannerColumn>
      )}
      <TripTypeControl value={tripType} onChange={onTripType} />
      <PassengersStepper value={passengers} onChange={onPassengers} />
    </div>
  );
}

/** A linha da Ida: a origem, o destino (sem ele, o carro com motorista) e o
 *  Quando. Na corrida de cooperativa o Quando aparece mesmo sem horário («A
 *  definir»), e na ida e volta a linha ganha o título «Ida». `openField` é o
 *  campo que abre sozinho (só no card vivo, nunca no remontado); `whenField`,
 *  o Quando que o card deixa escolher, no lugar da pílula só leitura. */
export function OutboundRow({
  ui,
  shared,
  origin,
  destination,
  openField,
  tripMode,
  roundTrip,
  onOrigin,
  onDestination,
  whenField,
  searchButton,
}: Readonly<{
  ui: MobilityPlannerBlock;
  shared: PlaceFieldShared;
  origin: Place | undefined;
  destination: Place | undefined;
  openField: 'origin' | 'destination' | null;
  tripMode: boolean;
  roundTrip: boolean;
  onOrigin: (place: Place) => void;
  onDestination: (place: Place) => void;
  whenField: ReactNode;
  searchButton: ReactNode;
}>): ReactNode {
  const { t } = useGenUiI18n();
  return (
    <div className="grid gap-1">
      {roundTrip && <p className={`m-0 ${LABEL}`}>{t('mobilityPlanner.outbound')}</p>}
      <div className={ROW}>
        <PlannerPlaceField
          {...shared}
          label={t('mobilityPlanner.origin')}
          columnLabel={t('mobilityPlanner.origin')}
          field={ui.origin}
          value={origin}
          open={openField === 'origin'}
          onPick={onOrigin}
        />
        {ui.destination && (
          <PlannerPlaceField
            {...shared}
            label={t('mobilityPlanner.destination')}
            columnLabel={t('mobilityPlanner.destination')}
            field={ui.destination}
            value={destination}
            open={openField === 'destination'}
            onPick={onDestination}
          />
        )}
        {whenField}
        {!whenField && (ui.when !== undefined || tripMode) && (
          <WhenColumn
            label={t('mobilityPlanner.when')}
            {...(ui.when ? { when: ui.when } : {})}
            pending={t('mobilityPlanner.whenPending')}
          />
        )}
        {searchButton}
      </div>
    </div>
  );
}

/** A linha da Volta: largo, «Volta» e as três colunas sem rótulo (as pontas
 *  nascem invertidas e se trocam, o horário é só leitura); celular, a rota no
 *  título e só o horário. A dica diz de onde veio o horário. */
export function ReturnRow({
  ui,
  shared,
  backOrigin,
  backDestination,
  onBackOrigin,
  onBackDestination,
  searchButton,
}: Readonly<{
  ui: MobilityPlannerBlock;
  shared: PlaceFieldShared;
  backOrigin: Place | undefined;
  backDestination: Place | undefined;
  onBackOrigin: (place: Place) => void;
  onBackDestination: (place: Place) => void;
  searchButton: ReactNode;
}>): ReactNode {
  const { t } = useGenUiI18n();
  const back = t('mobilityPlanner.return');
  const wideOnly = '@max-[theme(--breakpoint-sm)]:hidden';
  return (
    <div className="grid gap-1">
      <p className={`m-0 ${LABEL} ${wideOnly}`}>{back}</p>
      <p className={`m-0 hidden ${LABEL} @max-[theme(--breakpoint-sm)]:block`}>
        {t('mobilityPlanner.returnRoute', {
          from: backOrigin?.title ?? shared.emptyText,
          to: backDestination?.title ?? shared.emptyText,
        })}
      </p>
      <div className={ROW}>
        <PlannerPlaceField
          {...shared}
          label={`${back} · ${t('mobilityPlanner.origin')}`}
          className={wideOnly}
          field={ui.return?.origin ?? {}}
          value={backOrigin}
          open={false}
          onPick={onBackOrigin}
        />
        <PlannerPlaceField
          {...shared}
          label={`${back} · ${t('mobilityPlanner.destination')}`}
          className={wideOnly}
          field={ui.return?.destination ?? {}}
          value={backDestination}
          open={false}
          onPick={onBackDestination}
        />
        <WhenColumn
          label={`${back} · ${t('mobilityPlanner.when')}`}
          {...(ui.return?.when ? { when: ui.return.when } : {})}
          pending={t('mobilityPlanner.whenPending')}
          showLabel={false}
        />
        {searchButton}
      </div>
      {ui.return?.hint && (
        <p className="m-0 text-caption text-muted-foreground">{ui.return.hint}</p>
      )}
    </div>
  );
}
